import { Helmet } from 'react-helmet';
import { createTheme, ThemeProvider } from 'flowbite-react';
import { RouterProvider } from 'react-router-dom';
import './App.css';
import { UploadManager } from './component/UploadManager/UploadManager';
import { router } from './routes/router';
import { TourProvider } from './tour';
import ThemedToastContainer from './component/ThemedToastContainer';

const customTheme = createTheme({
  spinner: {
    base: 'inline animate-spin text-gray-200 dark:text-gray-600',
  },
  modal: {
    // Magpiie design system — modals sit on `surface-elev` with the kit's
    // `rounded-modal` radius (16px) and `shadow-modal`. `!bg-surface-elev`
    // overrides Flowbite's default `bg-white dark:bg-gray-700`. Header /
    // footer borders use `border-outline` so they flip with theme.
    root: {
      show: {
        // Backdrop — DS uses a slightly purpler/darker scrim than Flowbite's
        // default gray. `!` overrides the `bg-gray-900/50 dark:bg-gray-900/80`.
        on: 'flex !bg-black/60',
      },
    },
    content: {
      inner:
        '!bg-surface-elev rounded-modal !shadow-modal flex relative max-h-[90dvh] flex-col',
    },
    header: {
      base: 'flex items-start justify-between border-b !border-outline py-3 px-4 items-center',
      title: '!text-foreground text-sm font-semibold',
      close: {
        base: 'ml-auto inline-flex items-center rounded-control bg-transparent p-1.5 text-sm !text-foreground-muted hover:!bg-surface-2 hover:!text-foreground',
        icon: 'h-5 w-5',
      },
    },
    body: {
      base: 'flex-1 overflow-auto p-6 !text-foreground',
    },
    footer: {
      base: 'flex items-center space-x-2 rounded-b border-t !border-outline p-4',
    },
  },
  button: {
    color: {
      alternative: 'border-[1.5px]',
      ghost:
        'bg-transparent text-foreground hover:bg-primary-700/10 hover:text-primary-700 focus:ring-0',
      // Magpiie design system — deep-purple brand primary action
      brand:
        'bg-primary text-white shadow-btn hover:bg-primary/90 focus:ring-2 focus:ring-primary/40',
      // Magpiie design system — outlined neutral (Cancel/Back/secondary).
      // Mirrors DS `compareBtn` / `iconBtn`: bg=var(--surface), border=var(--border).
      outline:
        'bg-surface text-foreground border border-outline hover:bg-surface-2 focus:ring-2 focus:ring-primary/20',
      // Magpiie design system — destructive action (filled). DS-token coral
      // instead of Flowbite's stock `bg-red-700`. Pair with `outline` prop
      // for the subtler outlined-coral variant (DS `btnGhost` + coral
      // pattern), which lives under `outlineColor.failure` below.
      failure:
        'bg-danger text-white shadow-btn hover:bg-danger/90 focus:ring-2 focus:ring-danger/40',
    },
    outlineColor: {
      // Magpiie design system — outlined destructive. Use as
      // `<Button color="failure" outline>`. Matches DS row-level delete /
      // transfer buttons (coral text + 30%-opacity coral border,
      // soft coral wash on hover).
      failure:
        'bg-transparent text-danger border border-danger/30 hover:bg-danger/10 focus:ring-2 focus:ring-danger/30',
      // Magpiie design system — outlined brand. Use as
      // `<Button color="brand" outline>`. Mirror of the filled brand button.
      brand:
        'bg-transparent text-primary border border-primary/40 hover:bg-primary/10 focus:ring-2 focus:ring-primary/30',
    },
    size: {
      // Magpiie design system — sm/md/lg triplet matches design's three button shapes; chip is the inline filter trigger.
      // chip — 9/14/8/12/700 — inline page action (RevisionsProject rf.primary, ChannelBanner CTA)
      chip: 'py-[9px] px-3.5 text-xs font-bold rounded-btn',
      // sm — 9/12/8/12/600 — small dense secondary action (rf.secondary)
      sm: 'py-[9px] px-3 text-xs font-semibold rounded-btn',
      // md — 9/18/8/13/700 — modal CTA / form submit (M.primaryBtn)
      md: 'py-[9px] px-[18px] text-[13px] font-bold rounded-btn',
      // lg — 11/24/8/16/700 — auth + onboarding hero CTA (.btn-lg)
      lg: 'py-[11px] px-6 text-base font-bold rounded-btn',
    },
  },
  textInput: {
    field: {
      input: {
        // Magpiie design system — `color="brand"` opts in to design-system styling.
        // Default `color="gray"` left untouched so existing Flowbite usage is unchanged.
        colors: {
          brand:
            'border-outline-strong bg-surface-elev text-foreground placeholder:text-foreground-dim rounded-input focus:border-primary focus:ring-primary',
          // `color="bare"` — borderless transparent input that inherits its
          // background from the surrounding surface. Use inside a card row or
          // any container that already supplies its own chrome.
          bare: 'border-none bg-transparent text-foreground placeholder:text-foreground-muted focus:border-transparent focus:ring-0',
          // `color="failure"` — invalid input. DS-tokenised so the border /
          // focus ring use our `danger` (coral) instead of Flowbite's red.
          failure:
            'border-danger bg-surface-elev text-foreground placeholder:text-foreground-dim rounded-input focus:border-danger focus:ring-danger',
        },
      },
    },
  },
  textarea: {
    colors: {
      brand:
        'border border-outline-strong bg-surface-elev text-foreground placeholder:text-foreground-dim rounded-input focus:border-primary focus:ring-primary',
      // `color="bare"` — borderless transparent textarea for use inside an
      // already-styled card. Mirrors the kit's notes textarea pattern.
      bare: 'border-none bg-transparent text-foreground placeholder:text-foreground-muted focus:border-transparent focus:ring-0',
      // `color="failure"` — invalid textarea, matches `textInput.failure`.
      failure:
        'border border-danger bg-surface-elev text-foreground placeholder:text-foreground-dim rounded-input focus:border-danger focus:ring-danger',
    },
  },
  select: {
    field: {
      select: {
        colors: {
          brand:
            'border-outline-strong bg-surface-elev text-foreground rounded-input focus:border-primary focus:ring-primary',
        },
      },
    },
  },
  dropdown: {
    floating: {
      // Magpiie design system — popover surface that flips correctly in dark
      // `!` overrides Flowbite's default `dark:bg-gray-700` / `dark:text-white`
      base: 'z-10 w-fit divide-y rounded-card border border-outline !bg-surface-elev !text-foreground shadow-popover divide-outline focus:outline-none',
      target: 'w-fit',
      item: {
        base: 'flex w-full cursor-pointer items-center justify-start px-4 py-2 text-sm !text-foreground hover:!bg-surface-2 focus:!bg-surface-2 focus:outline-none',
        icon: 'mr-2 h-4 w-4',
      },
      header: 'block px-4 py-2 text-sm !text-foreground-muted',
      divider: 'my-1 h-px !bg-outline',
    },
  },
  badge: {
    // Magpiie design system — `color="chip"` opts in to DS-tokenised chip
    // styling for reviewer/tag/draft-version chips. Uses `nav-hover` so the
    // chip sits *on top of* a surface-elev parent (kit `dsS.tagChip` pattern);
    // `surface-2` would render as a darker hole in dark mode. Default colors
    // are untouched so existing semantic uses (success/warning/etc.) still work.
    root: {
      color: {
        chip: '!bg-nav-hover !text-foreground hover:!bg-nav-hover',
      },
    },
  },
  datepicker: {
    // Magpiie design system — replaces Flowbite's gray defaults so both the
    // inline Datepicker (in popovers) and the standalone trigger flip with
    // the active theme.
    popup: {
      root: {
        base: 'absolute top-10 z-50 block pt-2',
        inline: 'relative top-0 z-auto',
        inner:
          'inline-block rounded-card !bg-surface-elev p-3 !shadow-modal !text-foreground',
      },
      header: {
        base: '',
        title:
          'px-2 py-2 text-center text-[12px] font-semibold !text-foreground',
        selectors: {
          base: 'mb-2 flex justify-between',
          button: {
            base: 'rounded-card px-3 py-1.5 text-[12px] font-semibold !text-foreground hover:!bg-surface-2 focus:outline-none',
            prev: '',
            next: '',
            view: '',
          },
        },
      },
      view: { base: 'p-1' },
      footer: {
        base: 'mt-2 flex space-x-2',
        button: {
          base: 'w-full rounded-card px-3 py-1.5 text-center text-[11px] font-medium focus:outline-none',
          today: '!bg-primary !text-white hover:!bg-primary-deep',
          clear:
            'border !border-outline bg-transparent !text-foreground hover:!bg-surface-2',
        },
      },
    },
    views: {
      days: {
        header: {
          base: 'mb-1 grid grid-cols-7',
          title:
            'h-6 text-center text-[10.5px] font-medium leading-6 !text-foreground-muted',
        },
        items: {
          base: 'grid w-56 grid-cols-7',
          item: {
            base: 'block flex-1 cursor-pointer rounded-[6px] border-0 text-center text-[12px] font-semibold leading-8 !text-foreground hover:!bg-surface-2',
            selected: '!bg-primary !text-white hover:!bg-primary-deep',
            disabled: '!text-foreground-dim',
          },
        },
      },
      months: {
        items: {
          base: 'grid w-56 grid-cols-4',
          item: {
            base: 'block flex-1 cursor-pointer rounded-[6px] border-0 text-center text-[12px] font-semibold leading-8 !text-foreground hover:!bg-surface-2',
            selected: '!bg-primary !text-white hover:!bg-primary-deep',
            disabled: '!text-foreground-dim',
          },
        },
      },
      years: {
        items: {
          base: 'grid w-56 grid-cols-4',
          item: {
            base: 'block flex-1 cursor-pointer rounded-[6px] border-0 text-center text-[12px] font-semibold leading-8 !text-foreground hover:!bg-surface-2',
            selected: '!bg-primary !text-white hover:!bg-primary-deep',
            disabled: '!text-foreground-dim',
          },
        },
      },
      decades: {
        items: {
          base: 'grid w-56 grid-cols-4',
          item: {
            base: 'block flex-1 cursor-pointer rounded-[6px] border-0 text-center text-[12px] font-semibold leading-8 !text-foreground hover:!bg-surface-2',
            selected: '!bg-primary !text-white hover:!bg-primary-deep',
            disabled: '!text-foreground-dim',
          },
        },
      },
    },
  },
  checkbox: {
    // DS spec (magpiie-ui-ux/ui_kits/app/LoginScreen.jsx Checkbox primitive):
    // 18x18, rounded-[5px], 1.5px outline-strong border when unchecked,
    // bg-primary + white check when checked.
    base: 'h-[18px] w-[18px] appearance-none rounded-[5px] border-[1.5px] border-outline-strong bg-surface bg-[length:0.6em_0.6em] bg-center bg-no-repeat checked:border-transparent checked:bg-current checked:bg-check-icon focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface',
    color: {
      // Route every common color slot to DS primary so callers using
      // color="brand" / "default" / "purple" all share the brand fill.
      default: 'text-primary focus:ring-primary',
      brand: 'text-primary focus:ring-primary',
      purple: 'text-primary focus:ring-primary',
    },
  },
  toggleSwitch: {
    toggle: {
      checked: {
        // On-state (knob translated right) — DS uses bg=var(--primary).
        // No `!important` so the off-state class below can win when unchecked.
        on: 'after:translate-x-full after:border-transparent rtl:after:-translate-x-full bg-primary',
        // Off-state — DS uses bg=var(--border) instead of Flowbite's gray-200.
        off: '!bg-outline after:border-outline-strong',
        color: {
          // Map Flowbite color slots to DS primary so existing `color="purple"`
          // / `color="default"` callers all share the brand on-state color.
          default: 'bg-primary',
          purple: 'bg-primary',
          brand: 'bg-primary',
        },
      },
    },
  },
  sidebar: {
    root: {
      inner: 'bg-surface-3',
    },
    logo: {
      base: 'justify-center',
    },
    collapse: {
      button: 'text-greyModerate',
    },
    item: {
      collapsed: {
        insideCollapse: 'px-3',
      },
      base: 'rounded-lg px-3 py-2 text-primary-700 hover:bg-primary-700/10 hover:text-primary-700 focus:outline-none focus:ring-0 cursor-pointer font-medium text-sm',
      active: 'bg-primary-700/10 text-primary-700',
      content: { base: 'flex items-center gap-3' },
      icon: { base: 'text-primary-700 h-4 w-4' },
      label: '',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <TourProvider>
        <div className="App">
          <Helmet>
            <title>Magpiie - Video Production Management Software</title>
            <meta
              name="description"
              content="Magpiie is a comprehensive video Production Management software tailored for YouTube creators, addressing the complexities of content production from ideation to publishing"
            />
            <meta
              name="keywords"
              content="Meta, magpiie, video-production, Metapher, management, content creation, youtube creators"
            />
          </Helmet>
          <UploadManager />
          <RouterProvider router={router} />
          <ThemedToastContainer />
        </div>
      </TourProvider>
    </ThemeProvider>
  );
}
export default App;
